import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  getAllVendors,
  approveVendor,
  rejectVendor,
} from '../../redux/slices/vendorSlice';
import { Search, CheckCircle, XCircle, Clock, Users } from 'lucide-react';
import { toast } from 'sonner';

const AdminVendors = () => {
  const dispatch = useDispatch();
  const { vendors, loading, error } = useSelector((state) => state.vendor);

  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    sortBy: 'createdAt',
  });

  const [rejectModal, setRejectModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    dispatch(getAllVendors(filters));
  }, [dispatch, filters]);

  const handleApprove = async (vendorId) => {
    try {
      await dispatch(approveVendor(vendorId)).unwrap();
      toast.success('Vendor approved successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to approve vendor');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      await dispatch(
        rejectVendor({ vendorId: rejectModal, reason: rejectionReason })
      ).unwrap();
      toast.success('Vendor application rejected');
      setRejectModal(null);
      setRejectionReason('');
    } catch (err) {
      toast.error(err.message || 'Failed to reject vendor');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, text: 'Pending' },
      approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle, text: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, text: 'Rejected' },
      suspended: { color: 'bg-gray-100 text-gray-700', icon: XCircle, text: 'Suspended' },
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${badge.color}`}>
        <Icon className="w-4 h-4" />
        {badge.text}
      </span>
    );
  };

  const stats = {
    total: vendors?.length || 0,
    pending: vendors?.filter((v) => v.status === 'pending').length || 0,
    approved: vendors?.filter((v) => v.status === 'approved').length || 0,
    rejected: vendors?.filter((v) => v.status === 'rejected').length || 0,
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Vendor Management</h1>
        <p className="text-gray-600 mt-1">
          Manage vendor applications and store accounts
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Vendors</p>
              <p className="text-2xl font-bold mt-1">{stats.total}</p>
            </div>
            <Users className="w-8 h-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-yellow-50 p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-700 text-sm font-medium">Pending</p>
              <p className="text-2xl font-bold mt-1 text-yellow-700">{stats.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        <div className="bg-green-50 p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-700 text-sm font-medium">Approved</p>
              <p className="text-2xl font-bold mt-1 text-green-700">{stats.approved}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-red-50 p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-700 text-sm font-medium">Rejected</p>
              <p className="text-2xl font-bold mt-1 text-red-700">{stats.rejected}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="suspended">Suspended</option>
          </select>

          <select
            value={filters.sortBy}
            onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="createdAt">Newest First</option>
            <option value="name">Name (A-Z)</option>
            <option value="revenue">Revenue (High to Low)</option>
            <option value="orders">Orders (High to Low)</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Vendors Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      ) : vendors.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-bold mb-2">No Vendors Found</h3>
          <p className="text-gray-600">No vendor applications to display</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Store Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Stats
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {vendors.map((vendor) => (
                <tr key={vendor._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium">{vendor.storeName}</p>
                      <p className="text-sm text-gray-600">{vendor.businessName}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Applied: {new Date(vendor.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm">{vendor.contactEmail}</p>
                    <p className="text-sm text-gray-600">{vendor.contactPhone}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <p>Products: {vendor.stats?.totalProducts || 0}</p>
                      <p>Orders: {vendor.stats?.totalOrders || 0}</p>
                      <p>Revenue: ₹{vendor.stats?.totalRevenue?.toLocaleString() || 0}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(vendor.status)}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-2">
                      {vendor.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApprove(vendor._id)}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => setRejectModal(vendor._id)}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                      <Link
                        to={`/admin/vendors/${vendor._id}`}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm text-center"
                      >
                        View Full Details
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Reject Vendor Application</h3>
            <p className="text-gray-600 mb-4">
              Please provide a reason for rejecting this application.
            </p>

            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={4}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black mb-4"
            />

            <div className="flex gap-4">
              <button
                onClick={() => {
                  setRejectModal(null);
                  setRejectionReason('');
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Reject Application
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVendors;
